import React, { useState, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import { InputWrap, SelectStyles, LabelWrapper, Error } from "./styled";
import { Label, useThemeUI } from "theme-ui";

import { ChevronIcon, TimesIcon } from "components/svg";


type Option = { value: string | number; label: string };

const normalizeToArray = (input: any): string[] => {
  if (input === null || typeof input === "undefined" || input === "") return [];
  if (Array.isArray(input)) {
    if (input.every((item) => typeof item === "object" && item !== null && "value" in item)) {
      return input.map((item) => `${item.value}`);
    }
    return input.map((item) => `${item}`);
  }
  if (typeof input === "string") {
    return input.split(",").map((token) => token.trim()).filter(Boolean);
  }
  return [`${input}`];
};

const mapValuesToOptions = (tokens: string[], options: Option[]) =>
  tokens
    .map((token) => options.find((option) => `${option.value}` === token))
    .filter(Boolean) as Option[];

const SelectInput = ({
  name,
  label,
  options = [],
  value,
  description,
  multiple,
  readonly,
  required,
  disabled,
  clearable,
  $customStyles,
  $responseErrors,
  $errors,
  searchable = false,
  onChange
}: {
  name?: string;
  label?: string | React.ReactNode;
  options?: any[];
  // Single-select passes a scalar; multi-select passes an array of values
  // (or of {value,label} objects). normalizeToArray handles all three shapes.
  value?: string | number | (string | number)[] | null;
  description?: string;
  required?: boolean;
  multiple?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  $customStyles?: any;
  $responseErrors?: any;
  $errors?: any;
  searchable?: boolean;
  onChange?: (e?: any) => void;
}) => {

  const selectRef = useRef<any>(null);
  const themeContext = useThemeUI();
  const { theme } = themeContext;
  const [borderError, setBorderError] = useState<boolean>(false); 

  
  const mappedValue = multiple
    ? mapValuesToOptions(normalizeToArray(value), options)
    : options.find((option: any) => option.value === value) || null;
  
  const setSelectValue = function (e: any){  
    if(disabled) return true;

    let changedValue = e?.value ?? ""
    if(multiple){
      const values = e.map((el: any) => el.value).filter((v: any) => v !== "");   
      changedValue = values; 
    }

    if(typeof onChange === "function"){
      onChange(changedValue ?? "")
    }
    setTimeout(() => { selectRef?.current?.blur(); },100);
  }

  useEffect(() => {
    if($responseErrors || $errors){
      setBorderError(true);
    }else{
      setBorderError(false);
    }
  },[$responseErrors, $errors]);

  const MultiValueRemove = (props: any) => {
    return (
      <components.MultiValueRemove {...props}>
        <TimesIcon height={"10px"} width={"10px"} fill={theme?.colors?.base_600} />
      </components.MultiValueRemove>
    );
  };

  const ClearIndicator = (props: any) => {
    return (      
      <components.ClearIndicator {...props}>        
        <TimesIcon height={"14px"} width={"14px"} fill={theme?.colors?.base_500} />        
      </components.ClearIndicator>
    );
  };

  const DropdownIndicator = (props: any) => {
    return (      
      <components.DropdownIndicator {...props}>        
        <ChevronIcon height={"14px"} width={"14px"} fill={theme?.colors?.dkgrey} transform={"rotate(-90deg)"} />        
      </components.DropdownIndicator>
    );
  };

  return (
    <InputWrap sx={$customStyles} $errors={borderError}>
      {label && 
        <LabelWrapper>
          <Label>{label}</Label>
          {required ? <span>*</span>  : ''}         
        </LabelWrapper>
      }
      <Select 
        ref={selectRef}
        styles={SelectStyles} 
        menuPlacement="auto"
        classNamePrefix="react-select"
        options={options} 
        name={name} 
        isSearchable
        isMulti={multiple}
        value={mappedValue}
        onChange={(e) => setSelectValue(e)}  
        components={{MultiValueRemove,ClearIndicator,DropdownIndicator}} 
        isClearable={multiple || clearable}
        isDisabled={disabled || readonly}  
        menuPortalTarget={document.body}     
      />  
      {description && <p><small>{description}</small></p>}         
      {$errors && <Error>{$errors}</Error>}
    </InputWrap>    
  );
};

export default SelectInput;
